import type { AppAbility } from '~/utils/ability'
import {
  resolveMissingPermissions,
  type PermissionRequirement,
} from '~/utils/permissionRequirement'

/**
 * Règle unique « une entrée de menu visible mène à un écran ouvrable » (audit du 19/09, BLOQ-01 à
 * BLOQ-04 et MAJ-07).
 *
 * Le menu latéral est visible dès qu'**un** des écrans du module est accessible
 * (`useNavItemVisibility` prend l'union des `staticData.permission` descendants en OU), alors que
 * chaque index portait jusqu'ici sa seule permission propre — d'où des entrées visibles menant à
 * « Accès refusé », et des fonctionnalités inatteignables faute d'autre point d'entrée.
 *
 * Deux primitives, à utiliser ensemble sur chaque index de module :
 * - `moduleIndexPermission` pour la garde de l'index (exactement l'union que voit le menu) ;
 * - `firstAuthorizedScreen` pour rediriger (ou n'afficher que) le premier écran autorisé.
 */

/** Un écran du module : sa route et l'exigence déclarée par son `staticData.permission`. */
export interface ModuleScreen<TTo extends string = string> {
  to: TTo
  permission: PermissionRequirement
}

/**
 * Garde d'un index de module : l'union en OU des exigences de ses écrans, la sienne comprise.
 *
 * Les exigences sont reprises telles quelles (pas d'aplatissement des ET) pour que la garde ne soit
 * jamais plus large que la visibilité du menu.
 */
export const moduleIndexPermission = (
  permissions: readonly PermissionRequirement[],
): { any: PermissionRequirement[] } => ({ any: [...permissions] })

/** L'exigence est-elle satisfaite par l'ability courante ? */
export const isPermissionSatisfied = (
  ability: AppAbility,
  permission: PermissionRequirement,
): boolean => resolveMissingPermissions(ability, permission).length === 0

/**
 * Premier écran autorisé d'un module, dans l'ordre de déclaration — cible de la redirection d'un
 * index sans contenu propre, ou onglet affiché par défaut.
 *
 * `undefined` quand aucun ne l'est : la garde de l'index a alors déjà refusé l'accès.
 */
export const firstAuthorizedScreen = <TScreen extends { permission: PermissionRequirement }>(
  ability: AppAbility,
  screens: readonly TScreen[],
): TScreen | undefined =>
  screens.find((screen) => isPermissionSatisfied(ability, screen.permission))
